import { HsVector3 } from '../../math-new/objects/vector3.class';
import { HsMatrix4 } from '../../math-new/objects/matrix4.class';
import type { HsCameraTool } from './camera-tool.interface';
import { HsGlobal } from '../global';
import { HsQuaternion } from '../../math-new/objects/quaternion.class';
import type { HsCamera } from '../../engine-new/objects/cameras/camera.class';
import { HsControlActionType } from '../actions/control-action-type.enum';
import type { HsControlAction } from '../actions/control-action.abstract';
import type { HsCanvasSize } from '../utils/canvas-size.class';
import { HsScrollControlAction } from '../actions/scroll-control-action.class';
import { HsZoomControlAction } from '../actions/zoom-control-action.class';
import { HsMouseMoveControlAction } from '../actions/mouse-move-control-action.class';
import { HsKeyboardControlAction } from '../actions/keyboard-control-action.class';
import { HsToolsConstants } from '../tools-constants.constants';
import { HsHypersceneConstants } from '../../math-new/constants/hyperscene-constants.global';
import { HsHypersceneMathUtils } from '../../math-new/functions/hyperscene-math-utils.class';
import { hsApplyTransformationToCamera } from './apply-transformation-to-camera.function';

const CAMERA_TO_TARGET_MIN_DISTANCE: number = 0.5;
const COEFFICIENT_DELTA_TIME_KEYBOARD: number = 0.06;

export class HsCameraRotationAroundTargetObject implements HsCameraTool {
  private readonly targetObjectPoint: HsVector3 = HsGlobal.ROTATE_POINT;

  private readonly verticalQuaternion: HsQuaternion = HsQuaternion.makeIdentityQuaternion();

  private readonly horizontalQuaternion: HsQuaternion = HsQuaternion.makeIdentityQuaternion();

  private frameStartTime: number | null = null;

  private frameEndTime: number | null = null;

  private deltaTime: number = 1;

  static readonly #vector3Temp1: HsVector3 = HsVector3.makeZeroVector();
  static readonly #vector3Temp2: HsVector3 = HsVector3.makeZeroVector();
  static readonly #vector3Temp3: HsVector3 = HsVector3.makeZeroVector();
  static readonly #matrix4Temp1: HsMatrix4 = HsMatrix4.makeIdentityMatrix();
  static readonly #quaternion4Temp1: HsQuaternion = HsQuaternion.makeIdentityQuaternion();

  constructor(
    private readonly camera: HsCamera,
    private readonly controlActionsMap: Map<HsControlActionType, HsControlAction>,
    private readonly canvasSize: HsCanvasSize | null
  ) {}

  public processControlActions(): void {
    this.frameStartTime = this.frameEndTime;
    this.frameEndTime = Date.now();
    this.deltaTime = this.frameStartTime === null ? this.deltaTime : this.frameEndTime - this.frameStartTime;

    const controlActions: IterableIterator<HsControlAction> = this.controlActionsMap.values();
    for (let index: number = 0; index < this.controlActionsMap.size; index++) {
      const element: IteratorResult<HsControlAction> = controlActions.next();
      const controlAction: HsControlAction | undefined = element.value;

      if (controlAction === undefined || !controlAction.isActive) {
        continue;
      }

      if (controlAction instanceof HsScrollControlAction) {
        this.processScrollControlAction(controlAction);
        continue;
      }

      if (controlAction instanceof HsZoomControlAction) {
        this.processZoomControlAction(controlAction);
        continue;
      }

      if (controlAction instanceof HsMouseMoveControlAction) {
        this.processMoveControlAction(controlAction);
        continue;
      }

      if (controlAction instanceof HsKeyboardControlAction) {
        this.processKeyboardControlAction(controlAction);
        continue;
      }
    }
  }

  private processScrollControlAction(scrollActon: HsScrollControlAction): void {
    const { scrollDelta }: HsScrollControlAction = scrollActon;
    this.updateCameraScrollPosition(scrollDelta);
  }

  private processZoomControlAction(zoomAction: HsZoomControlAction): void {
    const cameraMatrix: HsMatrix4 = HsCameraRotationAroundTargetObject.#matrix4Temp1.copy(this.camera.worldMatrix);
    const cameraPosition: HsVector3 = HsCameraRotationAroundTargetObject.#vector3Temp2.setFromMatrixPosition(
      this.camera.worldMatrix.values
    );
    const offset: HsVector3 = HsCameraRotationAroundTargetObject.#vector3Temp3
      .copy(cameraPosition)
      .subtract(this.targetObjectPoint);
    const currentDistance: number = offset.length();
    if (currentDistance === 0 || (currentDistance <= CAMERA_TO_TARGET_MIN_DISTANCE && zoomAction.scale > 1)) {
      return;
    }

    // Orbit tools zoom toward their rotation center rather than the point under the fingers.
    const nextDistance: number = Math.max(currentDistance / zoomAction.scale, CAMERA_TO_TARGET_MIN_DISTANCE);
    offset.multiplyByScalar(nextDistance / currentDistance);
    cameraPosition.copy(this.targetObjectPoint).add(offset);
    cameraMatrix.setPosition(cameraPosition.values);

    hsApplyTransformationToCamera(this.camera, cameraMatrix);
  }

  private processMoveControlAction(moveAction: HsMouseMoveControlAction): void {
    switch (moveAction.actionType) {
      case HsControlActionType.LeftMove:
        this.rotateCameraOnLeftMoveAction(moveAction);
        return;
      default:
        return;
    }
  }

  private rotateCameraOnLeftMoveAction(moveActon: HsMouseMoveControlAction): void {
    if (this.targetObjectPoint === null) {
      return;
    }

    this.initializeQuaternionHelpers();

    const renderCanvasRatio: number = this.getRenderCanvasRatio();
    const coefficient: number =
      HsToolsConstants.MOUSE_MOVE_ROTATE_FACTOR * HsHypersceneConstants.DEG_2_RAD * this.deltaTime;

    const actionDeltaY: number = moveActon.delta.y;
    const rotationDeltaAroundX: number = actionDeltaY * coefficient * renderCanvasRatio;
    this.rotateVerticalHelper(rotationDeltaAroundX);

    const actionDeltaX: number = moveActon.delta.x;
    const rotationDeltaAroundY: number = actionDeltaX * coefficient * renderCanvasRatio;
    this.rotateHorizontalHelper(rotationDeltaAroundY);

    this.updateCameraPosition();
  }

  private processKeyboardControlAction(keyboardControlAction: HsKeyboardControlAction): void {
    this.initializeQuaternionHelpers();
    const coefficient: number = this.deltaTime * COEFFICIENT_DELTA_TIME_KEYBOARD;

    switch (keyboardControlAction.actionType) {
      case HsControlActionType.PressedTop:
        this.rotateVerticalHelper(-HsHypersceneConstants.DEG_2_RAD * coefficient);
        this.updateCameraPosition();
        break;
      case HsControlActionType.PressedBottom:
        this.rotateVerticalHelper(HsHypersceneConstants.DEG_2_RAD * coefficient);
        this.updateCameraPosition();
        break;
      case HsControlActionType.PressedRight:
        this.rotateHorizontalHelper(2 * HsHypersceneConstants.DEG_2_RAD * coefficient);
        this.updateCameraPosition();
        break;
      case HsControlActionType.PressedLeft:
        this.rotateHorizontalHelper(-2 * HsHypersceneConstants.DEG_2_RAD * coefficient);
        this.updateCameraPosition();
        break;
      case HsControlActionType.PressedAdditionalOne:
        this.updateCameraScrollPosition(2);
        break;
      case HsControlActionType.PressedAdditionalTwo:
        this.updateCameraScrollPosition(-2);
        break;
      default:
        return;
    }
  }

  private initializeQuaternionHelpers(): void {
    const cameraQuaternion: HsQuaternion = HsCameraRotationAroundTargetObject.#quaternion4Temp1.setFromRotationMatrix(
      this.camera.worldMatrix.values
    );
    this.verticalQuaternion.set(cameraQuaternion.x, 0, 0, cameraQuaternion.w).normalize();
    this.horizontalQuaternion.set(0, cameraQuaternion.y, 0, cameraQuaternion.w).normalize();
  }

  private rotateVerticalHelper(rotationDeltaAroundX: number): void {
    const axisX: HsVector3 = HsCameraRotationAroundTargetObject.#vector3Temp1.set(1, 0, 0);
    const deltaXQuaternion: HsQuaternion = HsCameraRotationAroundTargetObject.#quaternion4Temp1.setFromAxisAngle(
      axisX.values,
      rotationDeltaAroundX
    );

    this.verticalQuaternion.multiply(deltaXQuaternion);

    const eulerAngles: HsVector3 = HsHypersceneMathUtils.convertToEulerAngles(
      this.verticalQuaternion,
      HsCameraRotationAroundTargetObject.#vector3Temp2
    );
    const xAngleToCheck: number = eulerAngles.x;

    if (xAngleToCheck > HsToolsConstants.MAX_CAMERA_ANGLE) {
      this.verticalQuaternion.setFromAxisAngle(axisX.values, HsToolsConstants.MAX_CAMERA_ANGLE);
    }
    if (xAngleToCheck < HsToolsConstants.MIN_CAMERA_ANGLE) {
      this.verticalQuaternion.setFromAxisAngle(axisX.values, HsToolsConstants.MIN_CAMERA_ANGLE);
    }
  }

  private rotateHorizontalHelper(rotationDeltaAroundY: number): void {
    const axisY: HsVector3 = HsCameraRotationAroundTargetObject.#vector3Temp1.set(0, 1, 0);
    const deltaQuaternion: HsQuaternion = HsCameraRotationAroundTargetObject.#quaternion4Temp1.setFromAxisAngle(
      axisY.values,
      rotationDeltaAroundY
    );
    this.horizontalQuaternion.multiply(deltaQuaternion);
  }

  private updateCameraScrollPosition(scrollDelta: number): void {
    const cameraMatrix: HsMatrix4 = HsCameraRotationAroundTargetObject.#matrix4Temp1.copy(this.camera.worldMatrix);
    const cameraPosition: HsVector3 = HsCameraRotationAroundTargetObject.#vector3Temp2.setFromMatrixPosition(
      this.camera.worldMatrix.values
    );
    const distance: number = HsVector3.distance(this.targetObjectPoint, cameraPosition);

    HsCameraRotationAroundTargetObject.#vector3Temp3.copy(cameraPosition);
    HsCameraRotationAroundTargetObject.#vector3Temp3.subtract(this.targetObjectPoint);

    const currentDistance: number = HsCameraRotationAroundTargetObject.#vector3Temp3.length();

    if (currentDistance < CAMERA_TO_TARGET_MIN_DISTANCE && scrollDelta < 0) {
      return;
    }

    HsCameraRotationAroundTargetObject.#vector3Temp3.normalize();

    const deltaProgress: number =
      scrollDelta < 0 ? scrollDelta / 100 - Math.abs(distance) / 10 : scrollDelta / 100 + Math.abs(distance) / 10;
    HsCameraRotationAroundTargetObject.#vector3Temp3.multiplyByScalar(deltaProgress);

    cameraPosition.add(HsCameraRotationAroundTargetObject.#vector3Temp3);
    cameraMatrix.setPosition(cameraPosition.values);

    hsApplyTransformationToCamera(this.camera, cameraMatrix);
  }

  private updateCameraPosition(): void {
    this.horizontalQuaternion.multiply(this.verticalQuaternion);

    const cameraMatrix: HsMatrix4 = this.camera.worldMatrix;
    const cameraPosition: HsVector3 = HsCameraRotationAroundTargetObject.#vector3Temp2.setFromMatrixPosition(
      cameraMatrix.values
    );
    const direction: HsVector3 = HsCameraRotationAroundTargetObject.#vector3Temp1.set(0, 0, 1);
    direction.applyQuaternion(this.horizontalQuaternion.values).normalize();
    const distance: number = HsVector3.distance(this.targetObjectPoint, cameraPosition);
    direction.multiplyByScalar(distance);

    const newPosition: HsVector3 = HsCameraRotationAroundTargetObject.#vector3Temp2.copy(this.targetObjectPoint);
    newPosition.add(direction);

    const scale: HsVector3 = HsCameraRotationAroundTargetObject.#vector3Temp1.set(1, 1, 1);
    HsCameraRotationAroundTargetObject.#matrix4Temp1.compose(
      newPosition.values,
      this.horizontalQuaternion.values,
      scale.values
    );
    hsApplyTransformationToCamera(this.camera, HsCameraRotationAroundTargetObject.#matrix4Temp1);
  }

  private getRenderCanvasRatio(): number {
    const newSize: HsCanvasSize | null = this.canvasSize;
    if (newSize !== null && newSize.renderWidth < newSize.realWidth) {
      return newSize.renderWidth / newSize.realWidth;
    }
    return 1;
  }
}
